import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, TrendingUp, Search } from "lucide-react";
import SectionHeading from "../components/SectionHeading";
import ServiceCard from "../components/ServiceCard";
import StatCard from "../components/StatCard";
import DifferenceBlock from "../components/DifferenceBlock";
import LeadForm from "../components/LeadForm";
import NewsletterForm from "../components/NewsletterForm";
import {
  hero,
  services,
  brandQuote,
  differenceBlocks,
  ctaSections,
  stats,
  images,
  newsletter,
} from "../data/siteData";

const Home = () => {
  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-p3-cream via-white to-blue-50">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-16 lg:py-24 grid lg:grid-cols-2 gap-12 items-center relative z-10">
          <div>
            <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl text-p3-navy leading-[1.1] mb-6">
              {hero.headline[0]}
              <br />
              {hero.headline[1]}
              <br />
              {hero.headline[2]}
              <span className="text-p3-blue">{hero.headlineHighlight}</span>
            </h1>
            <p className="text-lg text-p3-muted mb-8 max-w-md">
              It&apos;s all about <span className="font-semibold text-p3-navy">cash flow</span> management &{" "}
              <span className="font-semibold text-p3-navy">Minimizing</span> risks
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to={hero.ctaPrimary.href}
                className="px-7 py-3.5 rounded-lg bg-white border border-gray-200 shadow-sm text-p3-navy font-semibold hover:shadow-md transition-all"
              >
                {hero.ctaPrimary.label.toUpperCase()}
              </Link>
              <Link
                to={hero.ctaSecondary.href}
                className="px-7 py-3.5 rounded-lg bg-p3-gold text-p3-navy font-semibold hover:bg-p3-gold/90 transition-all shadow-sm"
              >
                {hero.ctaSecondary.label.toUpperCase()}
              </Link>
              <Link
                to="/shop"
                className="px-7 py-3.5 rounded-lg bg-p3-navy text-white font-semibold hover:bg-p3-navy-dark transition-all shadow-sm"
              >
                SHOP NOW
              </Link>
            </div>
          </div>
          <div className="relative">
            <div className="rounded-3xl overflow-hidden shadow-2xl">
              <img src={images.heroIllustration} alt="Amazon growth strategy" className="w-full h-80 lg:h-96 object-cover" />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-lg p-4 hidden sm:flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-p3-blue/10 flex items-center justify-center">
                <TrendingUp size={20} className="text-p3-blue" />
              </div>
              <div>
                <p className="text-xs text-p3-muted">Monthly Growth</p>
                <p className="font-heading font-bold text-p3-navy">+38%</p>
              </div>
            </div>
            <div className="absolute -top-6 -right-4 bg-white rounded-xl shadow-lg p-4 hidden sm:flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-p3-gold/15 flex items-center justify-center">
                <Search size={20} className="text-p3-gold" />
              </div>
              <div>
                <p className="text-xs text-p3-muted">Listing Score</p>
                <p className="font-heading font-bold text-p3-navy">92/100</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section id="services" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <SectionHeading eyebrow="What we do" title="Our Services" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((s) => (
              <ServiceCard key={s.slug} title={s.title} body={s.body} icon={s.icon} />
            ))}
          </div>
        </div>
      </section>

      {/* Brand quote */}
      <section className="py-16 bg-p3-navy">
        <div className="max-w-4xl mx-auto px-5 text-center">
          <p className="font-heading text-xl md:text-2xl font-semibold text-white leading-relaxed">
            &ldquo;{brandQuote}&rdquo;
          </p>
        </div>
      </section>

      {/* Difference blocks */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-5 lg:px-8">
          <SectionHeading eyebrow="Why choose us" title="We are a team of Amazon Experts" center />
          <div>
            {differenceBlocks.map((d, i) => (
              <DifferenceBlock key={d.title} index={i + 1} title={d.title} body={d.body} reverse={i % 2 === 1} />
            ))}
          </div>
        </div>
      </section>

      {/* Get ahead CTA */}
      <section className="py-16 bg-gradient-to-r from-p3-navy to-p3-navy-dark">
        <div className="max-w-5xl mx-auto px-5 text-center">
          <h2 className="font-heading font-extrabold text-3xl text-white mb-3">{ctaSections.getAhead.headline}</h2>
          <p className="text-white/70 mb-7 max-w-xl mx-auto">{ctaSections.getAhead.body}</p>
          <Link
            to={ctaSections.getAhead.href}
            className="inline-flex items-center gap-2 bg-p3-gold hover:bg-p3-gold/90 text-p3-navy font-semibold px-8 py-3.5 rounded-lg transition-all"
          >
            {ctaSections.getAhead.cta.toUpperCase()} <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* Stats */}
      <section className="py-20 bg-p3-cream">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <SectionHeading eyebrow={stats.subheadline} title={stats.headline} center />
          <div className="grid sm:grid-cols-3 gap-6">
            {stats.items.map((s) => (
              <StatCard key={s.label} value={s.value} label={s.label} />
            ))}
          </div>
        </div>
      </section>

      {/* Free analysis + lead form */}
      <section id="free-analysis" className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <SectionHeading eyebrow="Get started" title={ctaSections.freeAnalysis.headline} />
            <p className="text-p3-muted mb-6 max-w-md">{ctaSections.freeAnalysis.body}</p>
            <img src={images.dashboard} alt="Amazon brand success dashboard" className="rounded-2xl shadow-lg w-full h-64 object-cover" />
          </div>
          <LeadForm />
        </div>
      </section>

      {/* Newsletter */}
      <section className="py-14 bg-p3-navy">
        <div className="max-w-4xl mx-auto px-5 flex flex-col md:flex-row items-center justify-between gap-6">
          <h3 className="font-heading font-bold text-xl text-white">{newsletter.title}</h3>
          <NewsletterForm />
        </div>
      </section>

      {/* Affiliate program link */}
      <section className="py-8 bg-p3-cream text-center">
        <Link
          to="/affiliate-program"
          className="inline-flex items-center gap-2 text-sm font-semibold text-p3-blue hover:text-p3-navy transition-colors"
        >
          Join Our Affiliate Program &mdash; Earn Healthy Commission <ArrowRight size={15} />
        </Link>
      </section>
    </div>
  );
};

export default Home;
